import React, { Component } from 'react';
import DatePicker from 'react-datepicker';

import { getSelectedBranchId } from '../utils/branch';

var config = process.env;

// Panel de catálogo e inventario (ENI-247).
//
// Reglas de presentación, iguales a las de la pantalla de ventas:
//   - un dato que no se puede calcular se muestra "—", NUNCA 0;
//   - lo que el backend marca como aproximado se muestra como aproximado, con el motivo.
//
// Casi todo son tablas y no gráficos a propósito: las preguntas de este panel son "cuáles" y
// "cuánto", que se leen mejor en una lista ordenada y accionable que en una barra.

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('de-DE', { maximumFractionDigits: 0 })}`);
const number = (v) => (v == null ? '—' : Number(v).toLocaleString('de-DE'));
const percent = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
// Horas legibles: hasta dos días el número que importa son las horas ("18 h" dice más que "0,8
// días"); de ahí para arriba, los días.
const hours = (v) => {
    if (v == null) return '—';
    const h = Number(v);
    return h < 48 ? `${Math.round(h)} h` : `${Math.round(h / 24)} días`;
};
const fechaCorta = (iso) => {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(String(iso))) return null;
    const [y, m, d] = String(iso).split('-');
    return `${d}/${m}/${y}`;
};

function toIsoDate(date) {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

class InventoryAnalyticsComponent extends Component {
    constructor(props) {
        super(props);
        const hoy = new Date();
        const desde = new Date();
        desde.setMonth(desde.getMonth() - 3);

        this.state = {
            // 'value' = qué liquidar primero; 'lastSale' = qué nunca salió. Dos preguntas.
            ordenParado: 'value',
            paginaParado: 1, from: desde, to: hoy, deadStockDays: 90, loaded: false, recargando: false, exportando: false, error: null, data: null };
    }

    componentDidMount() { this.cargar(); }

    /**
     * `parcial: true` recarga SIN desmontar la pantalla.
     *
     * Ordenar o paginar el stock parado ponía `loaded: false`, que reemplaza toda la página por
     * "Cargando…": el navegador pierde el scroll y al volver el contenido queda arriba de todo.
     * Cambiar el orden de una tabla que está al fondo mandaba al principio en cada clic.
     *
     * Ojo con el `onClick={this.cargar}` de Aplicar: le llega el evento como primer argumento.
     * Por eso la opción va en un objeto — un evento no tiene `.parcial`, así que cae en completa,
     * que es lo correcto para un cambio de fechas (cambia toda la pantalla, no una tabla).
     */
    cargar = (opciones = {}) => {
        const parcial = opciones.parcial === true;
        this.setState(parcial ? { recargando: true, error: null } : { loaded: false, error: null });
        const branchId = getSelectedBranchId();
        const params = new URLSearchParams({
            from: toIsoDate(this.state.from),
            to: toIsoDate(this.state.to),
            deadStockDays: String(this.state.deadStockDays),
        });
        params.set('deadStockSort', this.state.ordenParado);
        params.set('deadStockPage', String(this.state.paginaParado));
        if (branchId) params.set('branchId', branchId);

        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}analytics/inventory?${params.toString()}`, {
            method: 'GET',
            headers: { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
        })
            .then((r) => r.json())
            .then((body) => {
                if (body.error > 0) {
                    if (body.error === 1002) { window.location.href = localStorage.getItem('homePageURL'); return; }
                    this.setState({ loaded: true, recargando: false, error: body.message, data: null });
                    return;
                }
                this.setState({ loaded: true, recargando: false, data: body.data, error: null });
            })
            .catch((e) => this.setState({ loaded: true, recargando: false, error: e.message, data: null }));
    };

    // Ordenar y paginar sólo cambian una tabla: la pantalla se queda donde está.
    recargarParado = () => this.cargar({ parcial: true });

    // Aplicar cambia el universo, así que vuelve a la página 1. Sin esto, estando en la página 12
    // y pasando la ventana de 90 días a 1 año (450 parados → 250, o sea 10 páginas), la tabla
    // decía "Nada parado en la ventana elegida" con el pie "Página 12 de 10": un vacío falso.
    aplicar = () => this.setState({ paginaParado: 1 }, this.cargar);

    /**
     * Exporta el stock parado COMPLETO, no la página que se está viendo.
     *
     * Desde que la tabla se paginó, `data.deadStock.items` son 25 de 450: el usuario leía "450
     * productos parados" en el pie, apretaba Exportar para armar la liquidación y se llevaba 25
     * sin ningún aviso. Es la lista con la que se decide qué rematar.
     *
     * Se pide de a 200, que es el tope del backend: 450 productos son 3 vueltas.
     */
    exportarCsv = async () => {
        const { data } = this.state;
        if (!data) return;
        this.setState({ exportando: true });

        const POR_TANDA = 200;
        const branchId = getSelectedBranchId();
        const todos = [];
        try {
            const vueltas = Math.max(1, Math.ceil((data.deadStock.total || 0) / POR_TANDA));
            for (let pagina = 1; pagina <= vueltas; pagina++) {
                const params = new URLSearchParams({
                    from: toIsoDate(this.state.from),
                    to: toIsoDate(this.state.to),
                    deadStockDays: String(this.state.deadStockDays),
                    deadStockSort: this.state.ordenParado,
                    deadStockPage: String(pagina),
                    deadStockLimit: String(POR_TANDA),
                });
                if (branchId) params.set('branchId', branchId);
                const r = await fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}analytics/inventory?${params.toString()}`, {
                    method: 'GET',
                    headers: { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
                });
                const body = await r.json();
                if (body.error > 0) throw new Error(body.message || 'No se pudo exportar');
                todos.push(...(body.data.deadStock.items || []));
            }
        } catch (e) {
            this.setState({ exportando: false });
            // Mejor no bajar nada que bajar un archivo incompleto que se va a leer como completo.
            window.alert(`No se pudo exportar el stock parado: ${e.message}`);
            return;
        }
        this.setState({ exportando: false });

        const filas = [['Código', 'Producto', 'Stock', 'Costo unitario', 'Valor a costo', 'Última venta']];
        todos.forEach((i) => {
            filas.push([i.code, `"${(i.name || '').replace(/"/g, '""')}"`, i.stock,
                i.cost == null ? '' : Math.round(i.cost), i.valueAtCost == null ? '' : Math.round(i.valueAtCost),
                i.lastSale || 'nunca']);
        });
        const csv = '﻿' + filas.map((f) => f.join(';')).join('\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `stock-parado-${toIsoDate(new Date())}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    renderRanking(titulo, items, columna, formato, ayuda) {
        return (
            <div className="col-lg-6 col-md-12 mb-4">
                <div className="card card-small h-100">
                    <div className="card-header border-bottom">
                        <h6 className="m-0">{titulo}</h6>
                        {ayuda && <span className="text-muted" style={{ fontSize: 12 }}>{ayuda}</span>}
                    </div>
                    <div className="card-body p-0">
                        <table className="table table-sm mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0">Producto</th>
                                    <th className="border-0 text-right">Unidades</th>
                                    <th className="border-0 text-right">Ingreso</th>
                                    <th className="border-0 text-right">Margen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 && <tr><td colSpan="4" className="text-center text-muted py-3">Sin ventas en el período.</td></tr>}
                                {items.slice(0, 10).map((i) => (
                                    <tr key={i.code}>
                                        <td>{i.name}</td>
                                        <td className="text-right">{number(i.units)}</td>
                                        <td className="text-right">{money(i.revenue)}</td>
                                        <td className="text-right">{money(i.margin)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { loaded, error, data } = this.state;

        return (
            <div>
                <div className="card card-small mb-4">
                    <div className="card-body py-3">
                        <div className="d-flex flex-wrap align-items-end">
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Ventas desde</label>
                                <DatePicker selected={this.state.from} onChange={(d) => this.setState({ from: d })} dateFormat="dd/MM/yyyy" className="form-control" />
                            </div>
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Hasta</label>
                                <DatePicker selected={this.state.to} onChange={(d) => this.setState({ to: d })} dateFormat="dd/MM/yyyy" className="form-control" />
                            </div>
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Sin vender hace</label>
                                <select className="form-control" value={this.state.deadStockDays} onChange={(e) => this.setState({ deadStockDays: Number(e.target.value) })}>
                                    <option value={30}>30 días</option>
                                    <option value={90}>90 días</option>
                                    <option value={180}>180 días</option>
                                    <option value={365}>1 año</option>
                                </select>
                            </div>
                            <div className="mb-2">
                                <button className="btn btn-primary mr-2" onClick={this.aplicar}>Aplicar</button>
                                <button className="btn btn-white" onClick={this.exportarCsv} disabled={!data || this.state.exportando}>{this.state.exportando ? 'Exportando…' : 'Exportar stock parado'}</button>
                            </div>
                        </div>
                    </div>
                </div>

                {error && <div className="alert alert-warning">{error}</div>}
                {!loaded && <div className="text-center text-muted py-5">Cargando…</div>}

                {loaded && data && (
                    <div>
                        {/* ── Capital inmovilizado ── */}
                        <div className="row">
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="card card-small h-100"><div className="card-body p-3">
                                    <span className="text-uppercase text-muted d-block" style={{ fontSize: 11 }}>Inmovilizado a costo</span>
                                    <h6 className="my-2">{money(data.stock.valueAtCost)}</h6>
                                    {/* Cuánto NO se pudo valorizar va pegado al número, no escondido. */}
                                    {data.stock.productsWithoutCost > 0 && (
                                        <span className="d-block text-muted" style={{ fontSize: 11 }}>
                                            {number(data.stock.productsWithoutCost)} producto(s) sin costo cargado quedaron fuera
                                        </span>
                                    )}
                                </div></div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="card card-small h-100"><div className="card-body p-3">
                                    <span className="text-uppercase text-muted d-block" style={{ fontSize: 11 }}>A precio de venta</span>
                                    <h6 className="my-2">{money(data.stock.valueAtPrice)}</h6>
                                </div></div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="card card-small h-100"><div className="card-body p-3">
                                    <span className="text-uppercase text-muted d-block" style={{ fontSize: 11 }}>Productos con stock</span>
                                    <h6 className="my-2">{number(data.stock.products)}</h6>
                                    <span className="d-block text-muted" style={{ fontSize: 11 }}>{number(data.stock.units)} unidades</span>
                                </div></div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="card card-small h-100"><div className="card-body p-3">
                                    <span className="text-uppercase text-muted d-block" style={{ fontSize: 11 }}>Parado</span>
                                    <h6 className="my-2">{money(data.deadStock.totalValueAtCost)}</h6>
                                    <span className="d-block text-muted" style={{ fontSize: 11 }}>
                                        {number(data.deadStock.total)} producto(s) sin vender hace {data.deadStock.days} días
                                    </span>
                                </div></div>
                            </div>
                        </div>

                        {/* ── Los dos rankings, uno al lado del otro: la gracia es compararlos ── */}
                        <div className="row">
                            {this.renderRanking('Más vendidos (por unidades)', data.ranking.topByUnits, 'units', number,
                                'Lo que más sale por la puerta.')}
                            {this.renderRanking('Los que más dejan (por margen)', data.ranking.topByMargin, 'margin', money,
                                'Casi nunca son los mismos: ahí está la decisión de compra.')}
                        </div>

                        {/* ── Stock parado ── */}
                        <div className="card card-small mb-4">
                            <div className="card-header border-bottom d-flex justify-content-between align-items-start flex-wrap">
                                <div>
                                    <h6 className="m-0">Stock parado</h6>
                                    <span className="text-muted" style={{ fontSize: 12 }}>
                                        Con stock y sin vender hace {data.deadStock.days} días:{' '}
                                        <strong>{number(data.deadStock.total)}</strong> productos,{' '}
                                        {number(data.deadStock.neverSold)} de ellos nunca se vendieron.
                                        {' '}El <strong>piso sin perder</strong> es hasta dónde se puede bajar
                                        el precio sin que la unidad deje pérdida.
                                    </span>
                                    {/* El piso se calcula con el canal MÁS CARO a propósito: si se
                                        fija el precio suponiendo mostrador y el juego sale por ML,
                                        cada unidad pierde. Los supuestos se muestran porque uno de
                                        ellos (la comisión) sale hoy de una muestra chica. */}
                                    {data.deadStock.assumptions && (
                                        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                            Descuenta el peor caso de una venta:{' '}
                                            {/* Dos decimales: con uno solo, 8,05% se muestra "8,0%" y
                                                la comisión es justo el supuesto que más mueve el piso. */}
                                            <strong>{(data.deadStock.assumptions.commissionPct * 100).toFixed(2)}%</strong> de comisión
                                            {' '}(MercadoLibre) + <strong>{money(data.deadStock.assumptions.shippingCost)}</strong> de envío
                                            {' '}+ <strong>{money(data.deadStock.assumptions.bagCost)}</strong> de bolsa, y el IVA
                                            {' '}sobre lo que queda de diferencia entre costo y venta.
                                            {!data.deadStock.assumptions.source.commissionFromConfig && (
                                                <> La comisión es la <strong>medida</strong> sobre las{' '}
                                                    {number(data.deadStock.assumptions.source.commissionSampleLines)} líneas
                                                    que tienen el dato cargado (ENI-243); cargando la real en configuración,
                                                    el piso se ajusta solo.</>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {/* Dos ordenamientos = dos preguntas. Por valor: qué liquidar
                                    primero. Por antigüedad: qué compré y nunca salió — un juego de
                                    $200 que nunca se vendió no aparece nunca ordenando por plata,
                                    y es justo el que hay que dejar de comprar. */}
                                <div className="btn-group btn-group-sm mt-1">
                                    {[['value', 'Por valor'], ['lastSale', 'Por antigüedad']].map(([k, t]) => (
                                        <button
                                            key={k}
                                            className={`btn ${this.state.ordenParado === k ? 'btn-primary' : 'btn-white'}`}
                                            onClick={() => this.setState({ ordenParado: k, paginaParado: 1 }, this.recargarParado)}
                                        >{t}</button>
                                    ))}
                                </div>
                            </div>
                            {/* Se atenúa en vez de vaciarse: así el clic tiene respuesta visible
                                sin que la página salte al principio. */}
                            <div className="card-body p-0" style={{ opacity: this.state.recargando ? 0.45 : 1 }}>
                                <table className="table table-sm mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0">Producto</th>
                                            <th className="border-0 text-right">Stock</th>
                                            <th className="border-0 text-right">Costo unit.</th>
                                            <th className="border-0 text-right">Precio de lista</th>
                                            {/* Las dos columnas que permiten decidir una liquidación:
                                                hasta dónde se puede bajar sin perder, y cuánto deja
                                                hoy cada unidad al precio de lista. */}
                                            <th className="border-0 text-right">Piso sin perder</th>
                                            <th className="border-0 text-right">Deja hoy</th>
                                            <th className="border-0 text-right">Valor a costo</th>
                                            <th className="border-0 text-right">Última venta</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.deadStock.items.length === 0 && <tr><td colSpan="8" className="text-center text-muted py-3">Nada parado en la ventana elegida.</td></tr>}
                                        {/* Sin `slice`: la página la arma el backend. Cortar acá además
                                            del LIMIT del servidor escondía filas que sí se pidieron. */}
                                        {data.deadStock.items.map((i) => (
                                            <tr key={i.code}>
                                                <td>{i.name}</td>
                                                <td className="text-right">{number(i.stock)}</td>
                                                <td className="text-right">{money(i.cost)}</td>
                                                <td className="text-right">{money(i.price)}</td>
                                                <td className="text-right">{money(i.breakEvenPrice)}</td>
                                                <td className={`text-right ${i.marginAtListPrice != null && i.marginAtListPrice < 0 ? 'text-danger font-weight-bold' : ''}`}>
                                                    {money(i.marginAtListPrice)}
                                                </td>
                                                <td className="text-right">{money(i.valueAtCost)}</td>
                                                {/* "nunca" no es lo mismo que "hace mucho": el backend los distingue. */}
                                                <td className="text-right">{i.lastSale || <span className="text-danger">nunca</span>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {/* Paginado: antes se mostraban 25 de 450 sin ninguna forma de ver el
                                    resto, y la lista se veía completa. */}
                                {data.deadStock.total > data.deadStock.limit && (
                                    <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top">
                                        <span className="text-muted" style={{ fontSize: 12 }}>
                                            Página {number(data.deadStock.page)} de{' '}
                                            {number(Math.ceil(data.deadStock.total / data.deadStock.limit))}
                                            {' · '}{number(data.deadStock.total)} productos parados
                                        </span>
                                        <div className="btn-group btn-group-sm">
                                            <button
                                                className="btn btn-white"
                                                disabled={data.deadStock.page <= 1}
                                                onClick={() => this.setState({ paginaParado: this.state.paginaParado - 1 }, this.recargarParado)}
                                            >Anterior</button>
                                            <button
                                                className="btn btn-white"
                                                disabled={data.deadStock.page >= Math.ceil(data.deadStock.total / data.deadStock.limit)}
                                                onClick={() => this.setState({ paginaParado: this.state.paginaParado + 1 }, this.recargarParado)}
                                            >Siguiente</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ── ABC + cobertura + merma ── */}
                        <div className="row">
                            <div className="col-lg-4 col-md-12 mb-4">
                                <div className="card card-small h-100">
                                    <div className="card-header border-bottom">
                                        <h6 className="m-0">ABC del catálogo</h6>
                                        <span className="text-muted" style={{ fontSize: 12 }}>Qué parte del catálogo hace la facturación.</span>
                                    </div>
                                    <div className="card-body">
                                        {['A', 'B', 'C'].map((clase) => (
                                            <p key={clase} className="mb-1">
                                                <strong>{clase}</strong>: {number(data.abc[clase].products)} producto(s) — {money(data.abc[clase].revenue)}
                                                {data.abc.total > 0 && ` (${percent((data.abc[clase].revenue / data.abc.total) * 100)})`}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4 col-md-12 mb-4">
                                <div className="card card-small h-100">
                                    <div className="card-header border-bottom">
                                        <h6 className="m-0">Precisión de inventario</h6>
                                        <span className="text-muted" style={{ fontSize: 12 }}>Del conteo cíclico del período.</span>
                                    </div>
                                    <div className="card-body">
                                        <p className="mb-1">{number(data.shrinkage.counts)} conteo(s), {percent(data.shrinkage.accuracyPct)} sin diferencia</p>
                                        <p className="mb-0">
                                            Diferencia: {number(data.shrinkage.unitDiff)} unidad(es), {money(data.shrinkage.valuedDiff)}
                                        </p>
                                        {data.shrinkage.counts === 0 && (
                                            <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                                                Sin conteos en el período: no hay precisión que reportar.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4 col-md-12 mb-4">
                                <div className="card card-small h-100">
                                    <div className="card-header border-bottom">
                                        <h6 className="m-0">Higiene de datos</h6>
                                        <span className="text-muted" style={{ fontSize: 12 }}>Para detectar si se degrada.</span>
                                    </div>
                                    <div className="card-body">
                                        <p className="mb-1">Costo en ventas: {percent(data.hygiene.salesCostCoveragePct)} del ingreso</p>
                                        <p className="mb-1">Catálogo sin costo: {number(data.hygiene.catalogWithoutCost)} de {number(data.hygiene.catalogProducts)}</p>
                                        <p className="mb-1">Comisión de ML: {percent(data.hygiene.mlFeeCoveragePct)} de {number(data.hygiene.mlChargeableLines)} línea(s) cobrable(s)</p>
                                        {/* Las bolsas se excluyen del panel por el flag `isBag`. Una
                                            bolsa a la que nadie se lo marcó se cuela igual y no hay
                                            forma de darse cuenta mirando los números — por eso se
                                            lista acá, con nombre, para poder ir a tildarla. */}
                                        {data.hygiene.unflaggedBags && data.hygiene.unflaggedBags.length > 0 && (
                                            <p className="mb-0 text-warning">
                                                Bolsas sin marcar como tal ({number(data.hygiene.unflaggedBags.length)}):{' '}
                                                {data.hygiene.unflaggedBags.map((b) => `${b.name} (${number(b.stock)} u.)`).join(', ')}
                                                {' '}— están contando como catálogo.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Reposición en estantería ──
                            La demora entre que un juego se vende en el mostrador y que otra copia
                            vuelve a la estantería. Un juego que está en stock pero no está a la
                            vista no se vende: esto mide cuánto dura ese estado.

                            Los tres bloques NO se suman ni se promedian juntos a propósito:
                            "repuesto" mide a los sobrevivientes (lo que nunca se repuso no tiene
                            fila y no entra), y "esperando stock" no es demora de la rutina sino un
                            quiebre de stock. Un solo número los taparía a los dos. */}
                        <div className="row">
                            <div className="col-12 mb-4">
                                <div className="card card-small">
                                    <div className="card-header border-bottom">
                                        <h6 className="m-0">Reposición en estantería</h6>
                                        <span className="text-muted" style={{ fontSize: 12 }}>
                                            Desde que se vende en el mostrador hasta que otra copia vuelve al salón.
                                            {data.shelfRestock && fechaCorta(data.shelfRestock.measurableFrom)
                                                ? ` Se mide desde el ${fechaCorta(data.shelfRestock.measurableFrom)} (antes de esa fecha son las ventas que la pantalla trajo al arrancar, no demora real).`
                                                : ''}
                                        </span>
                                    </div>
                                    <div className="card-body">
                                        {!data.shelfRestock ? (
                                            <p className="text-muted mb-0">
                                                Sin datos: la tabla de reposición todavía no existe en esta base
                                                (falta correr la migración).
                                            </p>
                                        ) : (
                                            <div className="row">
                                                <div className="col-lg-3 col-md-6 mb-3">
                                                    <span className="text-uppercase text-muted d-block" style={{ fontSize: 11 }}>Demora típica</span>
                                                    <h6 className="my-2">{hours(data.shelfRestock.done.medianHours)}</h6>
                                                    <span className="text-muted" style={{ fontSize: 12 }}>
                                                        Mediana de {number(data.shelfRestock.done.lines)} reposición(es)
                                                    </span>
                                                </div>
                                                <div className="col-lg-3 col-md-6 mb-3">
                                                    <span className="text-uppercase text-muted d-block" style={{ fontSize: 11 }}>9 de cada 10, antes de</span>
                                                    <h6 className="my-2">{hours(data.shelfRestock.done.p90Hours)}</h6>
                                                    <span className="text-muted" style={{ fontSize: 12 }}>
                                                        Peor caso: {hours(data.shelfRestock.done.maxHours)}
                                                    </span>
                                                </div>
                                                <div className="col-lg-3 col-md-6 mb-3">
                                                    {/* "De lo repuesto" y no "Repuesto en el día": el denominador
                                                        son las reposiciones marcadas, no las ventas. Con 2 de 10
                                                        ventas repuestas rápido y 8 nunca marcadas esto da 100 %,
                                                        y con el título anterior se leía como que se repuso todo
                                                        el mismo día. Por eso al lado va lo que sigue pendiente:
                                                        los dos números juntos son la foto. */}
                                                    <span className="text-uppercase text-muted d-block" style={{ fontSize: 11 }}>De lo repuesto, en el día</span>
                                                    <h6 className="my-2">{percent(data.shelfRestock.done.under24hOfDonePct)}</h6>
                                                    <span className="text-muted" style={{ fontSize: 12 }}>En menos de 24 h</span>
                                                </div>
                                                <div className="col-lg-3 col-md-6 mb-3">
                                                    {/* "Hoy" literal: es una foto de AHORA, no del período de arriba.
                                                        Cuando se filtraba por el rango, un período terminado ayer
                                                        escondía todo lo vendido hoy y los primeros 8 días decía
                                                        "Nada pendiente" con la lista llena. */}
                                                    <span className="text-uppercase text-muted d-block" style={{ fontSize: 11 }}>Sin reponer hoy</span>
                                                    <h6 className="my-2">{number(data.shelfRestock.pending.lines)}</h6>
                                                    <span className="text-muted" style={{ fontSize: 12 }}>
                                                        {data.shelfRestock.pending.lines > 0
                                                            ? `El más viejo hace ${hours(data.shelfRestock.pending.maxHours)}`
                                                            : 'Nada pendiente'}
                                                        {data.shelfRestock.waitingStock.lines > 0
                                                            ? ` · ${number(data.shelfRestock.waitingStock.lines)} esperando stock`
                                                            : ''}
                                                    </span>
                                                </div>
                                                {(data.shelfRestock.outsideWindow || data.shelfRestock.done.lines === 0) && (
                                                    <div className="col-12">
                                                        <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                                                            {data.shelfRestock.outsideWindow
                                                                ? `El período elegido termina antes del ${fechaCorta(data.shelfRestock.measurableFrom)}, que es desde cuándo se mide: no hay demora que reportar para esas fechas.`
                                                                : 'Todavía no hay reposiciones marcadas en el período: no hay demora que reportar.'}
                                                            {' '}Lo pendiente y lo que espera stock son de ahora, no del período, así que siguen siendo reales.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Días de cobertura ── */}
                        <div className="card card-small mb-4">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Días de cobertura</h6>
                                {/* Lo que el backend declara aproximado se muestra aproximado, con el motivo. */}
                                {data.coverage.approximate && (
                                    <span className="text-warning" style={{ fontSize: 12 }}>
                                        Aproximado — {data.coverage.approximateReason}
                                    </span>
                                )}
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0">Producto</th>
                                            <th className="border-0 text-right">Stock</th>
                                            <th className="border-0 text-right">Vendidas</th>
                                            <th className="border-0 text-right">Días de cobertura</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.coverage.items.length === 0 && <tr><td colSpan="4" className="text-center text-muted py-3">Sin datos.</td></tr>}
                                        {data.coverage.items.slice(0, 20).map((i) => (
                                            <tr key={i.code} className={i.belowMin ? 'table-warning' : ''}>
                                                <td>{i.name}{i.belowMin && <span className="text-danger" style={{ fontSize: 11 }}> · bajo mínimo</span>}</td>
                                                <td className="text-right">{number(i.stock)}</td>
                                                <td className="text-right">{number(i.unitsSold)}</td>
                                                {/* null = no vendió nada, no "dura para siempre". */}
                                                <td className="text-right">{i.daysOfCover == null ? '—' : number(i.daysOfCover)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* ── Proveedores ── */}
                        <div className="card card-small mb-4">
                            <div className="card-header border-bottom"><h6 className="m-0">Compras a proveedores</h6></div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0">Proveedor</th>
                                            <th className="border-0 text-right">Reposiciones</th>
                                            <th className="border-0 text-right">Gasto</th>
                                            <th className="border-0 text-right">Demora media</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.providers.length === 0 && <tr><td colSpan="4" className="text-center text-muted py-3">Sin reposiciones en el período.</td></tr>}
                                        {data.providers.map((p) => (
                                            <tr key={p.id}>
                                                <td>{p.name}</td>
                                                <td className="text-right">{number(p.restockings)}</td>
                                                <td className="text-right">{money(p.spent)}</td>
                                                {/* Sin pedidos enlazados no hay demora que promediar: "—", no 0. */}
                                                <td className="text-right">{p.avgLeadDays == null ? '—' : `${number(p.avgLeadDays)} día(s)`}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default InventoryAnalyticsComponent;
